import React, { useState, useRef } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grid,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { makeStyles } from '@material-ui/core/styles';

import documentProvider from '../../Providers/documentProvider';
import { changeMade, thereWasProblem } from '../../Utils/messages';
import useSelectDataProvider from '../../hooks/useSelectDataProvider';
import ContractUploadModal from './ContractUploadModal';
import PolicyUploadModal from './PolicyUploadModal';

const useStyles = makeStyles(() => ({
  root: {
    cursor: 'pointer',
    padding: '4px 10px 4px 10px',
    width: '100%',
  },
}));

interface DocumentType {
  id: number;
  name: string;
}

interface ContractData {
  dateSignature: string;
  periodDays: string;
  file: File | null;
}

interface PolicyData {
  uploadDeadline: string;
  file: File | null;
}

interface UploadI {
  basePath?: any;
  record?: any;
  segment?: string;
  related?: number;
  documentSegment: number;
  reserved?: boolean;
}

const DocumentUploadButton = ({
  basePath,
  record,
  segment,
  related,
  documentSegment,
  reserved = false,
}: UploadI) => {
  const filter = {
    segment: 'files',
    documentSegment,
  };
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const documentTypes = useSelectDataProvider(
    'document-types',
    filter
  ) as DocumentType[];
  const [selectedIndex, setSelectedIndex] = useState<number>();
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractData, setContractData] = useState<ContractData>({
    dateSignature: '',
    periodDays: '',
    file: null,
  });
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyData, setPolicyData] = useState<PolicyData>({
    uploadDeadline: '',
    file: null,
  });
  const anchorRef = useRef<HTMLDivElement>(null);
  const classes = useStyles();
  const menuWidth = anchorRef.current?.clientWidth;
  filter[reserved ? 'private' : 'private_ne'] = true;

  const handleClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOpen(false);
    setLoading(true);

    let data = {
      related,
      documentType: selectedIndex,
      segment,
      private: reserved,
    };
    const formData = new FormData();
    if (event.target.files?.length === 1) {
      const file = event.target.files[0];
      formData.append(`files.documents`, file, file.name);
    } else if (event.target.files) {
      for (let i = 0; i < event.target.files.length; i++) {
        const file = event.target.files[i];
        formData.append(`files.documents`, file, file.name);
      }
    }
    formData.append('data', JSON.stringify(data));

    documentProvider
      .uploadDocument(formData)
      .then(() => {
        notify("Carga de documento exitoso", 'success');
      })
      .catch((error) => {
        notify(
          `${thereWasProblem}: ${error.body?.data?.errors || 'Fallo general'}`,
          'warning'
        );
      })
      .finally(() => {
        setLoading(false);
        refresh();
      });
  };

  const handleMenuItemClick = (event: React.MouseEvent, index: number) => {
    setSelectedIndex(index);
    const selectedType = documentTypes.find((type) => type.id === index);

    const documentName = selectedType?.name?.trim();
    if (documentName === 'Contrato') {
      setShowContractModal(true);
    } else if (documentName === 'Póliza') {
      setShowPolicyModal(true);
    }
  };

  const handleContractSubmit = () => {
    if (!contractData.file) return;

    setLoading(true);
    const formData = new FormData();
    formData.append(
      'files.documents',
      contractData.file,
      contractData.file.name
    );
    formData.append(
      'data',
      JSON.stringify({
        related,
        documentType: selectedIndex,
        segment,
        private: reserved,
        dateSignature: contractData.dateSignature,
        periodDays: contractData.periodDays,
      })
    );

    documentProvider
      .uploadDocument(formData)
      .then((res) => {
        if (res.status !== 200) {
          notify(
            `Error en la carga del archivo, por favor inténtelo más tarde.`,
            'warning'
          );
        } else {
          notify('Contrato cargado correctamente', 'success');
          setShowContractModal(false);
          setContractData({
            dateSignature: '',
            periodDays: '',
            file: null,
          });
        }
      })
      .catch((error) => {
        notify(
          `${thereWasProblem}: ${error.body?.data?.errors || 'Fallo general'}`,
          'warning'
        );
      })
      .finally(() => {
        setLoading(false);
        refresh();
      });
  };

  const handlePolicySubmit = () => {
    if (!policyData.file) return;

    setLoading(true);
    const formData = new FormData();
    formData.append('files.documents', policyData.file, policyData.file.name);
    formData.append(
      'data',
      JSON.stringify({
        related,
        documentType: selectedIndex,
        segment,
        private: reserved,
        uploadDeadline: policyData.uploadDeadline,
      })
    );

    documentProvider
      .uploadDocument(formData)
      .then((res) => {
        if (res.status !== 200) {
          notify(
            `Error en la carga del archivo, por favor inténtelo más tarde.`,
            'warning'
          );
        } else {
          notify('Póliza cargada correctamente', 'success');
          setShowPolicyModal(false);
          setPolicyData({
            uploadDeadline: '',
            file: null,
          });
        }
      })
      .catch((error) => {
        notify(
          `${thereWasProblem}: ${error.body?.data?.errors || 'Fallo general'}`,
          'warning'
        );
      })
      .finally(() => {
        setLoading(false);
        refresh();
      });
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<Document, MouseEvent>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  };

  return (
    <Grid container direction='column' alignItems='flex-end'>
      <Grid item xs={12}>
        <ButtonGroup
          aria-label='split button'
          color='secondary'
          ref={anchorRef}
          variant='contained'
          disabled={loading}
        >
          <Button onClick={handleToggle} startIcon={<CloudUploadIcon />}>
            Subir archivo
          </Button>
          <Button
            aria-controls={open ? 'split-button-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup='menu'
            aria-label='select merge strategy'
            onClick={handleToggle}
            size='small'
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>
        <Popper
          anchorEl={anchorRef.current}
          placement='top'
          disablePortal
          open={open}
          role={undefined}
          style={{ zIndex: 3 }}
          transition
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList
                    id='split-button-menu'
                    style={{
                      maxHeight: 400,
                      width: menuWidth ?? 'auto',
                      maxWidth: 600,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      boxSizing: 'border-box',
                      paddingRight: 4,
                    }}
                  >
                    {documentTypes.map((value) => (
                      <MenuItem
                        key={value?.name}
                        selected={value?.id === selectedIndex}
                        style={{
                          padding: '0px',
                          boxSizing: 'border-box',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        }}
                        onClick={(event) =>
                          handleMenuItemClick(event, value.id)
                        }
                        className={classes.root}
                      >
                        <label
                          className={classes.root}
                          htmlFor={`${value?.id}`}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '8px 10px',
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {value?.name}
                          </div>
                          {value?.name?.trim() !== 'Contrato' &&
                            value?.name?.trim() !== 'Póliza' && (
                              <input
                                hidden
                                id={`${value?.id}`}
                                name={`${value?.name}`}
                                onChange={handleClick}
                                title={value?.name}
                                type='file'
                                multiple
                              />
                            )}
                        </label>
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Grid>

      <ContractUploadModal
        open={showContractModal}
        onClose={() => setShowContractModal(false)}
        onSubmit={handleContractSubmit}
        contractData={contractData}
        setContractData={setContractData}
        loading={loading}
      />

      <PolicyUploadModal
        open={showPolicyModal}
        onClose={() => setShowPolicyModal(false)}
        onSubmit={handlePolicySubmit}
        policyData={policyData}
        setPolicyData={setPolicyData}
        loading={loading}
      />
    </Grid>
  );
};

export default DocumentUploadButton;
